/**
 * Act 1, beat 4 — ***The Service Corridor***.
 *
 * Spec §7: *"First real stealth test: three patrols, a pressure-plate door built
 * for boots, a hose to drag over a drain to kill her scent. Dogs introduced."*
 *
 * ── The one sentence this beat has to deliver ────────────────────────────────
 *
 * **There is nowhere in this corridor she is supposed to be, so staying unseen
 * stops being about *where* she is and becomes about *when* she moves.**
 *
 * `farrowingStealth.ts` states beat 2's grammar in its own header: *she is
 * invisible until she is out of place; a pig in a pen is not information.* This
 * room is that sentence inverted. There is no pen, no straw and no trough — every
 * metre of it is somewhere a pig cannot be — so concealment stops being a
 * property of the place and becomes a property of the moment. Three patrols
 * supply the moments. **This beat's cover is a clock.**
 *
 * Then the dog inverts it a second time, and that is the back half: a patrol can
 * be timed and a dog cannot, because a dog is not reading where she *is*. It is
 * reading where she *has been*. Her past becomes evidence, and the hose and the
 * drain are how she edits it.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `corridorDirector.ts` is the only file that knows any of
 * this is triangles, patrols, or a dog. Same rule as `born.ts`, `weakestLatch.ts`
 * and `otto.ts`, and the same consequence: if a stage in this file ever imports
 * Three, the beat has stopped being replayable in Node and the mistake will be
 * here rather than in the sequencer.
 *
 * **The spatial stages wait on flags rather than reading `ctx.self` against the
 * corridor's constants**, for the reason `otto.ts` gives: "she is deep enough
 * into the room" is a fact about a room, and the room is what should decide it.
 *
 * ── Capture is not a stage ───────────────────────────────────────────────────
 *
 * Spec §5.6: caught → a cut → she wakes dragged back, world progress persists,
 * alarm elevated. **No lives, no game over. The world just gets harder and she
 * just keeps going. That is the thesis**, and this is the first beat that can
 * state it. It can happen during `the-crossing`, `the-dog`, `wash-it-off` or
 * `the-door`, and it returns her to the top of `the-crossing` with the alarm one
 * higher — which is a *director* action against a beat that is still running,
 * not a stage in the list. See `CORRIDOR_CUES.dragged`.
 *
 * The **save file** is beat 5's work and is deliberately absent; see
 * `docs/beat-4-plan.md`'s named cut line and D-053.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids, shared between this beat and the world that places them.
 *
 * Two, and they are the two halves of the beat: one thing to *read* and one
 * thing to *do*. Beat 2 had two, beat 3 had one, and the count is not a target —
 * it is that this beat teaches exactly two things and each of them has a verb.
 */
export const CORRIDOR_IDS = {
  /**
   * A patrol, under `observe`. Holding it is `focus` (D-066), and this is that
   * camera's **third** customer and its first at a subject that is both moving
   * and dangerous — beat 2's worker was moving and harmless, beat 3's Otto was
   * dangerous to nothing and stationary.
   *
   * Spec §5.3 is what it is for: *she is not lucky; she is taking notes.*
   */
  patrol: 'corridor.patrol',
  /**
   * The wash-down nozzle, under `nudge`.
   *
   * Spec §7.4 says *"a hose to **drag** over a drain"*, and `drag` does not
   * exist. **It is nudged, not dragged** — see `docs/beat-4-plan.md` rule 5 and
   * the decision recorded for it. The valve is old, the nozzle is already lying
   * on the lip of the gully, and a shove is enough. Same shape as beat 2's
   * latch: an existing verb applied to an authored object that behaves like the
   * thing it is.
   */
  nozzle: 'corridor.nozzle',
} as const;

/** Story flags the GAME raises. All of them are things only the simulation knows. */
export const CORRIDOR_FLAGS = {
  /** She is far enough in that the fattening hall's door is behind her. */
  inCorridor: 'corridor.inCorridor',
  /**
   * The first patrol has walked past her while she was in cover.
   *
   * Both halves are required, and that is the teach: being in the bay is not
   * enough and neither is waiting. The corridor only lets you through on the
   * conjunction of a place and a moment.
   */
  firstPassed: 'corridor.firstPassed',
  /** She has reached the wash-down bay at the top of the long leg. */
  atWashdown: 'corridor.atWashdown',
  /**
   * The dog has stopped where she crossed and put its nose down.
   *
   * Raised by the director when the handler reaches his station beside the bay
   * and the animal comes off his heel — **not on a timer**. Stage 5 used to be
   * eleven authored seconds, and eleven seconds is a thing a corridor can spend
   * without a dog ever appearing in it, which is exactly what it did.
   */
  noseDown: 'corridor.noseDown',
  /**
   * Her trail behind her is broken and the dog has lost it.
   *
   * Raised by the director when the scent system reports no useful sample
   * between the dog and her — not when she nudges the nozzle. Turning the water
   * on is the *action*; this is the *consequence*, and a beat that ended on the
   * action would be a beat about pressing a button.
   */
  trailBroken: 'corridor.trailBroken',
  /** She is through the personnel door and out of the corridor. */
  throughDoor: 'corridor.throughDoor',
} as const;

/** Every cue this beat can emit. The director switches on these and nothing else. */
export const CORRIDOR_CUES = {
  /** Cold light after amber, and 2.6 m of ceiling after 3.4. She is at the door. */
  open: 'corridor.open',
  /** `walker` comes round the corner toward her. Nothing to work out yet. */
  firstPatrol: 'corridor.firstPatrol',
  /** He has gone by. The corridor is hers for as long as it is. */
  clear: 'corridor.clear',
  /** `observe` comes alive on the patrols. */
  offerPatrol: 'corridor.offerPatrol',
  /** All three are running. This is the crossing. */
  crossing: 'corridor.crossing',
  /**
   * The handler comes down the short leg and **the dog is with him.**
   *
   * The director owns the seconds that follow, because the shape of a dog
   * walking at heel, stopping, and putting its nose down where she crossed is
   * presentation and the beat has no business timing it in fixed steps.
   */
  handlerArrives: 'corridor.handlerArrives',
  /** It is off the lead. From here her past is evidence. */
  looseTheDog: 'corridor.looseTheDog',
  /** The valve is open. Water is spreading toward the gully. */
  waterRunning: 'corridor.waterRunning',
  /**
   * He whistles it back.
   *
   * Only on `WASH_PATIENCE`, and it is the most frightening line in the beat:
   * it says the dog was never the danger. The man was, and he was in charge of
   * it the whole time.
   */
  whistleItBack: 'corridor.whistleItBack',
  /** The far door, the mat, and a man who is about to stand on it. */
  theDoor: 'corridor.theDoor',
  /**
   * Caught. Screen dims, and she wakes at the near end with the alarm up.
   *
   * Emitted by the DIRECTOR, not by a stage — capture is not a stage. It is in
   * this table because the table is the whole cue vocabulary and a cue the
   * director invents on its own is a cue nothing can log or diff.
   */
  dragged: 'corridor.dragged',
  /** Fade to black. */
  fadeOut: 'corridor.fadeOut',
  /** The beat is over. */
  end: 'corridor.end',
} as const;

/**
 * How long the beat waits on a player who has not worked out a verb.
 *
 * Same number and same reasoning as beats 1, 2 and 3: spec §10 forbids
 * softlocks, and "never worked out that `observe` does something" is a softlock
 * with better manners. When a stage times out the verb is **not** taken away.
 */
const PATIENCE = 45;

/**
 * How long the beat waits on a player who has not found the water.
 *
 * Twice `PATIENCE`, because unlike `observe` this one has a dog attached and a
 * player under pressure searches worse, not better. On expiry the handler
 * whistles the dog back (`whistleItBack`) and the beat continues — **the puzzle
 * is not solved for her and the water is still there.** She simply stops being
 * hunted while she works it out, which is the difference between a game that
 * waits and a game that gives up on the player.
 */
const WASH_PATIENCE = 90;

/**
 * How long the beat waits for the handler to walk his round to the wash-down bay.
 *
 * Not a patience clock in the same sense as the two above: there is no verb to
 * discover here and nothing the player can do to hurry it. It is a **backstop**,
 * and its size is set by the worst case rather than by taste — his round is
 * roughly a minute end to end and stage 5 can begin at any point in it, so
 * anything under that would cut the shot off in ordinary play.
 *
 * If this ever expires, the beat continues without its most important frame.
 * That is the softlock rule (spec §10) doing its job and it should be treated as
 * a bug report about the staging, not as an acceptable outcome.
 */
const WATCH_PATIENCE = 75;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

/** A stage that waits for a flag and will not give up, because the flag is the goal. */
function waitUntil(id: string, flag: string, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag),
  };
}

export const SERVICE_CORRIDOR_BEAT: Beat = {
  number: 4,
  id: 'act1.serviceCorridor',
  stages: [
    // ── arrive ───────────────────────────────────────────────────────────────
    // Through the service door from the fattening hall. No camera hold: three
    // beats have now opened in gameplay and the fourth is not taking that back.
    //
    // The whole job of this stage is the cut — amber to cold, 3.4 m of ceiling
    // to 2.6, haze to none. It is the strongest zone-to-zone change in the act
    // and it needs nothing but for her to walk into it.
    waitUntil('arrive', CORRIDOR_FLAGS.inCorridor, CORRIDOR_CUES.open),

    // ── the first one ────────────────────────────────────────────────────────
    // `walker` comes round the corner toward her, and the junction bay is four
    // metres away and unlit. **This is the teach, and it is authored so that it
    // cannot be failed by a player who does the obvious thing.**
    //
    // `waitUntil` and not `waitFor`: there is no patience clock because there is
    // no verb to discover. If she stands in the open she is seen, and being seen
    // here costs her the corridor's first thirty seconds and nothing else — the
    // capture loop is the same loop it will be for the rest of the beat, met
    // once, cheaply, while the stakes are still a demonstration.
    waitUntil('the-first-one', CORRIDOR_FLAGS.firstPassed, CORRIDOR_CUES.firstPatrol),

    // ── read them ────────────────────────────────────────────────────────────
    // `observe` is offered on the patrols and holding it is `focus`. Spec §5.3:
    // she watches until a pattern is legible, because **she is not lucky, she is
    // taking notes.**
    //
    // A player who never holds it gets the stage after PATIENCE and loses
    // nothing they cannot get by watching — which is the honest position, since
    // this beat's patterns are three men walking in straight lines and the
    // corridor shows them off whether or not anybody presses a key. The *drawn*
    // mental map is filed against a later beat and this stage is what will feed
    // it when it comes.
    waitFor('read-them', CORRIDOR_IDS.patrol, PATIENCE, CORRIDOR_CUES.clear, CORRIDOR_CUES.offerPatrol),

    // ── the crossing ─────────────────────────────────────────────────────────
    // Eighteen metres, three patrols, three bays, one pipe lane. The stage is
    // the beat, and it is the first time this game has asked her to do anything
    // under real pressure.
    //
    // No patience clock, deliberately, and this is the one stage where that
    // needs defending: a timeout here would hand her the corridor she failed to
    // cross, which is the only thing in the beat that would actually teach the
    // wrong lesson. It is not a softlock either — capture returns her to the top
    // of this stage rather than ending anything, so the corridor is always
    // available to try again, one alarm level worse.
    waitUntil('the-crossing', CORRIDOR_FLAGS.atWashdown, CORRIDOR_CUES.crossing),

    // ── the dog ──────────────────────────────────────────────────────────────
    // The handler comes down the short leg with the dog at his heel. It passes.
    // Then it stops, at the place she crossed, and puts its nose down.
    //
    // **This was one held stage of eleven seconds and that was the defect.** A
    // hold is a bet that the world will have done the thing by the time the
    // clock runs out, and this world did not: the handler walked out of the room
    // instead of into it, the dog followed him through the door, and the stage
    // spent its eleven seconds on an empty corridor before advancing exactly as
    // though the player had watched a dog find her scent. Same failure as beat
    // 3's silent stages (D-087), one beat later — a stage that advances proves
    // nothing about what was on screen.
    //
    // So it waits for the thing itself. `WATCH_PATIENCE` is generous because the
    // handler's round is long and she may be watching him walk the whole of it;
    // if it ever expires the beat carries on without the shot, which is the
    // softlock rule (spec §10) and is not meant to be reachable.
    waitFor('the-dog', CORRIDOR_FLAGS.noseDown, WATCH_PATIENCE, CORRIDOR_CUES.handlerArrives),

    // ── and then nobody moves ────────────────────────────────────────────────
    // Four seconds of a dog with its head on the floor she walked over, a man
    // standing beside it looking the other way, and her a metre inside the only
    // cover in the bay. Nothing happens and nothing can be made to happen —
    // which is the difference between watching an animal and operating one, and
    // is the same argument Otto's demonstration makes.
    //
    // **The first sight of the dog is safe and the second is not.** This is the
    // second one, and it is the frame the whole beat is built toward.
    {
      ...hold('the-nose', 4),
      exit: (_ctx, emit): void => emit(CORRIDOR_CUES.looseTheDog),
    },

    // ── wash it off ──────────────────────────────────────────────────────────
    // The nozzle, the water, the gully. See `CORRIDOR_IDS.nozzle` on why this is
    // a nudge, and `CORRIDOR_FLAGS.trailBroken` on why the stage ends on the
    // consequence rather than on the action.
    //
    // `WASH_PATIENCE` and then the handler whistles it back. That is not the
    // beat solving the puzzle for her — the water is still there and so is the
    // gully. It is the beat declining to hunt somebody who is already lost.
    {
      ...waitFor('wash-it-off', CORRIDOR_FLAGS.trailBroken, WASH_PATIENCE),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(CORRIDOR_FLAGS.trailBroken)) emit(CORRIDOR_CUES.whistleItBack);
      },
    },

    // ── the door ─────────────────────────────────────────────────────────────
    // The mat is built for boots and she does not weigh what it wants. **The
    // answer is the beat's own thesis: she goes through on somebody else's
    // timing.** He steps on it, the door opens, he walks through, the mat
    // releases, and she has three seconds — from the pipe lane beside the frame,
    // which is the only place she can already be.
    //
    // No prompt, no highlight, no hint. The door opens for him twice before it
    // opens for her, and the player will have seen it happen and been told
    // nothing.
    //
    // `waitUntil` with no patience, and unlike `the-crossing` this one needs no
    // defending: the handler's round repeats, so the opportunity repeats. A
    // player who misses it waits for the next one, which is a corridor being a
    // corridor rather than a beat being stuck.
    waitUntil('the-door', CORRIDOR_FLAGS.throughDoor, CORRIDOR_CUES.theDoor),

    // ── out ──────────────────────────────────────────────────────────────────
    {
      ...hold('close', 4.5, CORRIDOR_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(CORRIDOR_CUES.end),
    },
  ],
};
